import React from 'react';
import { post } from '../utils/api';
import {useAuth} from '../context/AuthContext';

export default function ProspectOnboardingWorkflow({
  selected,
  showImportModal,
  setShowImportModal,
  importingDocument,
  setImportingDocument,
  importForm,
  setImportForm,
  showActivationModal,
  setShowActivationModal,
  activationSaving,
  setActivationSaving,
  activationForm,
  setActivationForm,
  reloadSelected,
  reloadList
}) {
  const {user}=useAuth();
  const directApproval=user?.role==='super_admin';
  if (!selected || !selected.onboarding) {
    return null;
  }

  const isAuditor = selected.onboarding.role === 'inventory_auditor';

  const required =
    selected.required_activation_documents || [];

  const documents =
    selected.secure_documents || [];

  const acceptedDocuments =
    documents.filter(
      document =>
        document.status === 'accepted'
    );

  const completedTemplateIds =
    new Set(
      acceptedDocuments.map(
        document => document.template_id
      )
    );

  const missingTemplates =
    required.filter(
      template =>
        !completedTemplateIds.has(
          template.id
        )
    );

  const uploadHistoricalDocument =
    async () => {
      if (
        !importForm.template_id ||
        !importForm.signed_date ||
        !importForm.file ||
        !importForm.import_reason.trim() ||
        !importForm.verification_notes.trim()
      ) {
        alert(
          'Complete the document, signing date, file, reason and verification notes.'
        );
        return;
      }

      if (
        importForm.file.size >
        15 * 1024 * 1024
      ) {
        alert(
          'The signed file exceeds the 15 MB limit.'
        );
        return;
      }

      if (
        !window.confirm(
          'Confirm that this is the genuine signed copy of the selected document for ' +
          selected.full_name +
          '.'
        )
      ) {
        return;
      }

      setImportingDocument(true);

      try {
        const body = new FormData();

        body.append(
          'prospect_id',
          selected.id
        );
        body.append(
          'onboarding_id',
          selected.onboarding.id
        );
        body.append(
          'template_id',
          importForm.template_id
        );
        body.append(
          'signed_date',
          importForm.signed_date
        );
        body.append(
          'import_reason',
          importForm.import_reason.trim()
        );
        body.append(
          'verification_notes',
          importForm.verification_notes.trim()
        );
        body.append(
          'file',
          importForm.file
        );

        const response = await fetch(
          '/api/documents/import-signed',
          {
            method: 'POST',
            headers: {
              Authorization:
                'Bearer ' +
                localStorage.getItem(
                  'bb_token'
                )
            },
            body
          }
        );

        const result =
          await response.json();

        if (!response.ok) {
          throw new Error(
            result.error ||
            'Signed document could not be imported'
          );
        }

        setShowImportModal(false);
        reloadSelected();

        alert(
          'Signed document imported and verified successfully.'
        );
      } catch (error) {
        alert(error.message);
      } finally {
        setImportingDocument(false);
      }
    };

  const requestActivation =
    async () => {
      if (
        !activationForm.actual_start_date ||
        !activationForm.reason.trim()
      ) {
        alert(
          'Enter the actual start date and activation reason.'
        );
        return;
      }

      setActivationSaving(true);

      try {
        await post(
          '/staff-activation/onboarding/' +
          selected.onboarding.id +
          (directApproval?'/approve':'/request'),
          {
            actual_start_date:
              activationForm.actual_start_date,
            reason:
              activationForm.reason.trim()
          }
        );

        reloadSelected();
        if(directApproval){setShowActivationModal(false);reloadList();}

        alert(
          directApproval?'BloansBooks account activated successfully.':'Activation submitted for Managing Director approval.'
        );
      } catch (error) {
        alert(error.message);
      } finally {
        setActivationSaving(false);
      }
    };

  const approveActivation =
    async () => {
      if (
        !window.confirm(
          'Approve account creation for ' +
          selected.full_name +
          (isAuditor ? ' under the Inventory Auditor contract?' : ' and begin compatibility testing?')
        )
      ) {
        return;
      }

      setActivationSaving(true);

      try {
        const result = await post(
          '/staff-activation/onboarding/' +
          selected.onboarding.id +
          '/approve',
          {
            reason:
              activationForm.reason.trim() ||
              (isAuditor ? 'Approved for Inventory Auditor contract activation' : 'Approved for compatibility-test account activation')
          }
        );

        setShowActivationModal(false);
        reloadSelected();
        reloadList();

        alert(
          result.message ||
          'BloansBooks account activated successfully.'
        );
      } catch (error) {
        alert(error.message);
      } finally {
        setActivationSaving(false);
      }
    };

  return (
    <>
      {showImportModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{maxWidth:560}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                Import Previously Signed Document
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={()=>
                  setShowImportModal(false)
                }
              >
                X
              </button>
            </div>

            <div className="modal-body">
              <div style={{
                background:'#FFF3DC',
                borderRadius:8,
                padding:12,
                marginBottom:16,
                fontSize:12,
                color:'#8B5E0A'
              }}>
                Import only a genuine document previously signed by {selected.full_name}.
                Each file receives protected storage and a SHA-256 checksum.
              </div>

              <div className="form-group">
                <label className="form-label">
                  Matching Employment Document *
                </label>
                <select
                  className="form-control"
                  value={importForm.template_id}
                  onChange={event=>
                    setImportForm({
                      ...importForm,
                      template_id:
                        event.target.value
                    })
                  }
                >
                  <option value="">
                    Select document
                  </option>
                  {missingTemplates.map(
                    template => (
                      <option
                        key={template.id}
                        value={template.id}
                      >
                        {template.title}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Original Signing Date *
                </label>
                <input
                  type="date"
                  className="form-control"
                  max={isAuditor ? undefined :
                    new Date()
                      .toISOString()
                      .split('T')[0]
                  }
                  value={importForm.signed_date}
                  onChange={event=>
                    setImportForm({
                      ...importForm,
                      signed_date:
                        event.target.value
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Signed File *
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={event=>
                    setImportForm({
                      ...importForm,
                      file:
                        event.target.files?.[0] ||
                        null
                    })
                  }
                />
                <div className="form-hint">
                  PDF, JPG or PNG only. Maximum 15 MB.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Import Reason *
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={importForm.import_reason}
                  onChange={event=>
                    setImportForm({
                      ...importForm,
                      import_reason:
                        event.target.value
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Verification Notes *
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Explain how the signed file and signer were verified."
                  value={
                    importForm.verification_notes
                  }
                  onChange={event=>
                    setImportForm({
                      ...importForm,
                      verification_notes:
                        event.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="modal-ftr">
              <button
                className="btn btn-outline"
                disabled={importingDocument}
                onClick={()=>
                  setShowImportModal(false)
                }
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={importingDocument}
                onClick={
                  uploadHistoricalDocument
                }
              >
                {importingDocument
                  ? 'Importing...'
                  : 'Verify & Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showActivationModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{maxWidth:620}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                {isAuditor ? 'Inventory Auditor Contract Activation' : 'Compatibility-Test Activation'}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={()=>
                  setShowActivationModal(false)
                }
              >
                X
              </button>
            </div>

            <div className="modal-body">
              <div style={{
                display:'grid',
                gridTemplateColumns:
                  '1fr 1fr',
                gap:10,
                marginBottom:16
              }}>
                <div style={{
                  background:
                    isAuditor || selected.guarantor?.status ===
                    'approved'
                      ? '#E8F5EB'
                      : '#FDEAEA',
                  borderRadius:8,
                  padding:12,
                  fontSize:12
                }}>
                  <strong>{isAuditor ? 'Auditor activation requirements' : 'Guarantor'}</strong>
                  <br/>
                  {isAuditor ? 'Accepted offer, NDA and Privacy Agreement; no separate compatibility assessment' : selected.guarantor?.status ===
                  'approved'
                    ? 'Approved and verified'
                    : 'Not approved'}
                </div>

                <div style={{
                  background:
                    missingTemplates.length === 0 &&
                    required.length > 0
                      ? '#E8F5EB'
                      : '#FFF3DC',
                  borderRadius:8,
                  padding:12,
                  fontSize:12
                }}>
                  <strong>
                    Required Documents
                  </strong>
                  <br/>
                  {required.length - missingTemplates.length}
                  {' of '}
                  {required.length}
                  {' accepted'}
                </div>
              </div>

              <div style={{
                border:
                  '1px solid #E8E0D4',
                borderRadius:8,
                padding:12,
                marginBottom:16
              }}>
                {required.map(template=>{
                  const document =
                    acceptedDocuments.find(
                      item =>
                        item.template_id ===
                        template.id
                    );

                  return (
                    <div
                      key={template.id}
                      style={{
                        display:'flex',
                        justifyContent:
                          'space-between',
                        gap:12,
                        padding:'7px 0',
                        borderBottom:
                          '1px solid #F2EFE7',
                        fontSize:12
                      }}
                    >
                      <span>
                        {template.title}
                      </span>
                      <strong style={{
                        color:document
                          ? '#2E6B3A'
                          : '#8F2E24'
                      }}>
                        {document
                          ? document
                              .historical_import
                            ? 'Imported'
                            : 'Accepted'
                          : 'Missing'}
                      </strong>
                    </div>
                  );
                })}
              </div>

              {isAuditor && <p style={{fontSize:12}}>
                No compatibility assessment applies. Account activation is available on or after the contract start date, once the required documents are accepted. Probation terms remain as agreed in the offer.
              </p>}
              <div className="form-group">
                <label className="form-label">
                  {isAuditor ? 'Contract Start Date *' : 'Actual Compatibility-Test Start Date *'}
                </label>
                <input
                  type="date"
                  readOnly={isAuditor}
                  className="form-control"
                  max={isAuditor ? undefined :
                    new Date()
                      .toISOString()
                      .split('T')[0]
                  }
                  value={
                    activationForm
                      .actual_start_date
                  }
                  onChange={event=>
                    setActivationForm({
                      ...activationForm,
                      actual_start_date:
                        event.target.value
                    })
                  }
                />
                <div className="form-hint">
                  {isAuditor ? 'Super Admin may activate access before this agreed contract date. The employment start date remains unchanged.' : 'This may precede the proposed date, but cannot be in the future.'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Activation Reason *
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={
                    activationForm.reason
                  }
                  onChange={event=>
                    setActivationForm({
                      ...activationForm,
                      reason:
                        event.target.value
                    })
                  }
                />
              </div>

              {selected.onboarding
                .activation_status ===
                'pending_md' && (
                <div style={{
                  background:'#FFF3DC',
                  borderRadius:8,
                  padding:12,
                  fontSize:12,
                  color:'#8B5E0A'
                }}>
                  Awaiting Managing Director approval.
                </div>
              )}
            </div>

            <div className="modal-ftr">
              <button
                className="btn btn-outline"
                disabled={activationSaving}
                onClick={()=>
                  setShowActivationModal(false)
                }
              >
                Cancel
              </button>

              {selected.onboarding
                .activation_status !==
                'pending_md' ? (
                <button
                  className="btn btn-primary"
                  disabled={activationSaving}
                  onClick={requestActivation}
                >
                  {activationSaving
                    ? 'Submitting...'
                    : directApproval?'Activate & Create Account':'Request Activation'}
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  disabled={activationSaving}
                  onClick={approveActivation}
                >
                  {activationSaving
                    ? 'Activating...'
                    : 'Approve & Create Account'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
